/**
 * ResponsiveTable Component
 *
 * A table component that adapts to mobile screens by offering a card view.
 * Includes view toggle and horizontal scroll fallback for complex tables.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Table2, LayoutGrid } from 'lucide-react';
import { Button } from './Button';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ResponsiveTableColumn<T = Record<string, any>> {
  key: string;
  header: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  sortable?: boolean;
  width?: string;
  hideOnMobile?: boolean; // Hide this column in card view
  primary?: boolean; // Show prominently in card view
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ResponsiveTableProps<T = Record<string, any>> {
  data: T[];
  columns: ResponsiveTableColumn<T>[];
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: string | ((row: T, index: number) => string);
  emptyMessage?: string;
  loading?: boolean;
  className?: string;
  headerClassName?: string;
  tableWrapperClassName?: string;
  defaultView?: 'table' | 'cards';
  showViewToggle?: boolean;
  stickyHeader?: boolean;
  striped?: boolean;
  hover?: boolean;
  compact?: boolean;
  /** Tabular figures for aligned numeric columns (default true). */
  tabularNums?: boolean;
  /** Tighter cell padding below md breakpoint (default false). */
  denseMobile?: boolean;
  keyExtractor?: (row: T, index: number) => string | number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ResponsiveTable<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  rowClassName,
  emptyMessage = 'No data available',
  loading = false,
  className,
  headerClassName,
  tableWrapperClassName,
  defaultView = 'table',
  showViewToggle = true,
  stickyHeader = false,
  striped = false,
  hover = true,
  compact = false,
  tabularNums = true,
  denseMobile = false,
  keyExtractor = (_, index) => index,
}: ResponsiveTableProps<T>) {
  const cellPadTh = clsx(
    'text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300',
    compact && 'px-3 py-2',
    !compact && denseMobile && 'px-3 py-2 md:px-6 md:py-3',
    !compact && !denseMobile && 'px-6 py-3'
  );
  const cellPadTd = clsx(
    'text-sm text-gray-900 dark:text-gray-100',
    tabularNums && 'tabular-nums',
    compact && 'px-3 py-2',
    !compact && denseMobile && 'px-3 py-2 md:px-6 md:py-4',
    !compact && !denseMobile && 'px-6 py-4'
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const [view, setView] = useState<'table' | 'cards'>(
    viewParam === 'cards' || viewParam === 'table' ? viewParam : defaultView
  );

  // Update URL when view changes
  const handleViewChange = (newView: 'table' | 'cards') => {
    setView(newView);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', newView);
    setSearchParams(newParams);
  };

  // Sync with URL param changes
  useEffect(() => {
    if (viewParam === 'cards' || viewParam === 'table') {
      setView(viewParam);
    }
  }, [viewParam]);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-200 dark:bg-dark-700" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center dark:border-dark-600 dark:bg-dark-800">
        <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {/* View Toggle */}
      {showViewToggle && (
        <div className="flex justify-end">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-dark-600">
            <Button
              variant={view === 'table' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange('table')}
              leftIcon={<Table2 className="h-4 w-4" />}
              className="rounded-r-none border-r border-gray-300 dark:border-dark-600"
            >
              Table
            </Button>
            <Button
              variant={view === 'cards' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange('cards')}
              leftIcon={<LayoutGrid className="h-4 w-4" />}
              className="rounded-l-none"
            >
              Cards
            </Button>
          </div>
        </div>
      )}

      {/* Table View */}
      {view === 'table' && (
        <div
          className={clsx(
            'overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-700',
            tableWrapperClassName
          )}
        >
          <table
            className={clsx(
              'min-w-full divide-y divide-gray-200 dark:divide-dark-700',
              tabularNums && 'tabular-nums'
            )}
          >
            <thead
              className={clsx(
                'bg-gray-50 dark:bg-dark-800',
                stickyHeader && 'sticky top-0 z-10',
                headerClassName
              )}
            >
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={clsx(cellPadTh, column.headerClassName)}
                    style={column.width ? { width: column.width } : undefined}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
              {data.map((row, index) => {
                const key = keyExtractor(row, index);
                const isClickable = !!onRowClick;
                const computedRowClassName =
                  typeof rowClassName === 'function' ? rowClassName(row, index) : rowClassName;

                return (
                  <tr
                    key={key}
                    onClick={() => onRowClick?.(row, index)}
                    className={clsx(
                      striped && index % 2 === 0 && 'bg-gray-50 dark:bg-dark-800/50',
                      hover && 'hover:bg-gray-100 dark:hover:bg-dark-800',
                      isClickable && 'cursor-pointer',
                      computedRowClassName
                    )}
                  >
                    {columns.map((column) => {
                      const value = row[column.key];
                      const content = column.render
                        ? column.render(value, row, index)
                        : (value ?? '-');

                      return (
                        <td
                          key={column.key}
                          className={clsx('whitespace-nowrap', cellPadTd, column.className)}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Card View */}
      {view === 'cards' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((row, index) => {
            const key = keyExtractor(row, index);
            const isClickable = !!onRowClick;
            const computedRowClassName =
              typeof rowClassName === 'function' ? rowClassName(row, index) : rowClassName;

            // Separate primary and secondary columns
            const primaryColumns = columns.filter((col) => col.primary && !col.hideOnMobile);
            const secondaryColumns = columns.filter((col) => !col.primary && !col.hideOnMobile);

            return (
              <div
                key={key}
                onClick={() => onRowClick?.(row, index)}
                className={clsx(
                  'rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow dark:border-dark-700 dark:bg-dark-800',
                  hover && 'hover:shadow-md',
                  isClickable && 'cursor-pointer',
                  computedRowClassName
                )}
              >
                {/* Primary columns - prominent display */}
                {primaryColumns.length > 0 && (
                  <div className="mb-3 space-y-2 border-b border-gray-200 pb-3 dark:border-dark-700">
                    {primaryColumns.map((column) => {
                      const value = row[column.key];
                      const content = column.render
                        ? column.render(value, row, index)
                        : (value ?? '-');

                      return (
                        <div key={column.key}>
                          <div className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                            {column.header}
                          </div>
                          <div
                            className={clsx(
                              'mt-1 text-base font-semibold text-gray-900 dark:text-white',
                              tabularNums && 'tabular-nums'
                            )}
                          >
                            {content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Secondary columns - compact grid */}
                {secondaryColumns.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    {secondaryColumns.map((column) => {
                      const value = row[column.key];
                      const content = column.render
                        ? column.render(value, row, index)
                        : (value ?? '-');

                      return (
                        <div key={column.key}>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {column.header}
                          </div>
                          <div
                            className={clsx(
                              'mt-0.5 text-sm font-medium text-gray-900 dark:text-white',
                              tabularNums && 'tabular-nums'
                            )}
                          >
                            {content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
